import React, {FC} from "react";
import {useAxios} from "../../utils/hooks";
import useSafeState from "../../utils/safe-state";
import {Trans} from "@lingui/macro";
import {Breadcrumb, Button, Divider, message, Popconfirm, Tooltip, Upload, UploadProps} from "antd";
import {
    DeleteTwoTone,
    PlusOutlined,
    UploadOutlined,
    DownloadOutlined,
    EditOutlined
} from "@ant-design/icons/lib";
import DtoTable from "../../components/dto-table/dto-table";
import ButtonWithIcon from "../../components/buttons/button-with-icon";
import MscStockModel from "../../models/mscstock/msc-stock-model";
import {openNotification} from "../../utils/openNotification";
import Drawer from "../../components/drawer/drawer";
import MscStockDrawer from "../../forms/msc-stock-form";


const MscStockScreen: FC = () => {

    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const [trigger, setTrigger] = useSafeState(false);
    const [mscStockLoading, setMscStockLoading] = useSafeState(false);
    const mscStockText = <Trans>Msc Stock</Trans>

    const mscStockDrawer = Drawer(
        {
            title: mscStockText,
            children:
                <MscStockDrawer onClose={() => {
                    setTrigger(function(prev) { return !prev; });
                }}/>,
            customClose: () => setTrigger(function(prev) { return !prev; })
        }
    );

    const handleDelete = (record: any) => {
        if (axiosInstance.current != null) {
            axiosInstance.current
                .delete('/resource/msc-stock/id?id=' + encodeURIComponent(record.id)
                )
                .then(() => {
                    openNotification('customSuccess', 'Successfully deleted.')
                    setTrigger(function(prev) { return !prev; });
                })
                .catch(function (error: any) {
                    openNotification('error', error);
                    setTrigger(function(prev) { return !prev; });
                });
        }
    };

    const action = (record: any) => {

        return (
            <>
                <Tooltip title={<Trans>Edit</Trans>}>
                    <EditOutlined className={"ActionButtons"} style={{color: "#40a9ff"}}
                                  onClick={() =>
                                      mscStockDrawer
                                          .open(
                                              "Edit",
                                              {
                                                  id: record.id,
                                                  item: record.item,
                                                  ddsAssignmentRule: record.ddsAssignmentRule,
                                                  quantity: record.quantity,
                                                  ddsNumber: record.ddsNumber,
                                                  ddsNumberValidFrom: record.ddsNumberValidFrom,
                                                  ddsNumberValidTo: record.ddsNumberValidTo,
                                                  version: record.version
                                              })
                                  }
                    />
                </Tooltip>
                <Tooltip title={<Trans>Remove</Trans>}>
                    <Popconfirm
                        title={<Trans>Are you sure want to delete record?</Trans>}
                        onConfirm={() => handleDelete(record)}
                        okText={<Trans>Yes</Trans>}
                        cancelText={<Trans>No</Trans>}>
                        <DeleteTwoTone className={"ActionButtons"} twoToneColor={"red"}/>
                    </Popconfirm>

                </Tooltip>

            </>
        )
    };

    const actions = {
        width: 100,
        render: (text: string, record: any) => action(record),
        title: '',
        fixed: 'right',
        align: 'center'
    };

    const handleDownloadXls = () => {
        if(axiosInstance.current != null){
            const FileDownload = require('js-file-download');
            axiosInstance.current({
                url: process.env.REACT_APP_API_BASE_URL + '/resource/msc-stock/download-template',
                method: 'GET',
                responseType: 'blob'
            }).then((response) => {
                FileDownload(response.data, 'mscStockUploadTemplate.xlsx');
            }).catch(function (error: any) {
                console.log(error);
            });
        }
    }

    const mscStockProps: UploadProps = {
        name: 'file',
        multiple: false,
        showUploadList: false,
        customRequest: async (options) => {
            const { file, onSuccess, onError } = options;
            if (!axiosInstance.current) return;

            setMscStockLoading(true);
            const formData = new FormData();
            formData.append('file', file as any);

            try {
                await axiosInstance.current.post('/resource/msc-stock/upload-xls', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                openNotification('customSuccess', 'File successfully uploaded');
                onSuccess && onSuccess({}, file as any);
            } catch (error) {
                openNotification('error', error);
                onError && onError(error as any);
            } finally {
                setMscStockLoading(false);
            }
        },
        onChange(info) {
            if (info.file.status === 'done') {
                setTrigger(function(prev) { return !prev; });
            }
        }
    };

    const header = (
        <>
            <Button type={'primary'} onClick={() => mscStockDrawer.open('Add new')}>
                <ButtonWithIcon icon={<PlusOutlined />} text={<Trans>Add new</Trans>} />
            </Button>
            <Divider type="vertical" style={{ fontSize: 'xxx-large' }} />
            <Button onClick={() => handleDownloadXls()}>
                <ButtonWithIcon
                    icon={<DownloadOutlined />}
                    text={<Trans>.xlsx template</Trans>}
                />
            </Button>
            <Divider type="vertical" style={{ fontSize: 'xxx-large' }} />
            <Upload {...mscStockProps}>
                <Button loading={mscStockLoading}>
                    <ButtonWithIcon icon={<UploadOutlined/>} text={<Trans>.xlsx upload</Trans>} />
                </Button>
            </Upload>
            <Divider type="vertical" style={{ fontSize: 'xxx-large' }} />
        </>
    );


    return (<>

            <>

                <Breadcrumb style={{marginBottom: 10}}>
                    <Breadcrumb.Item><Trans id={'Home'}/></Breadcrumb.Item>
                    <Breadcrumb.Item><Trans id={'Msc Stock'}></Trans></Breadcrumb.Item>
                </Breadcrumb>
                <DtoTable
                    tableHeader={header}
                    model={MscStockModel}
                    action={actions}
                    trigger={trigger}
                    allowRefreshButton={true}
                    allowExport={true}
                    pageSize={100}
                    scroll={{x: 1300, y: "65vh"}}/>
            </>
            {mscStockDrawer.component}
        </>
    )
}
export default MscStockScreen;
